/*!
 * Debugger for Snowplow v4.6.8 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { __spreadArray } from 'tslib';
import { payloadJsonProcessor, LOG_LEVEL, version, getSchemaParts } from '@snowplow/tracker-core';
import randomcolor from 'randomcolor';

/*
 * Copyright (c) 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 *    list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 *    this list of conditions and the following disclaimer in the documentation
 *    and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 *    contributors may be used to endorse or promote products derived from
 *    this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
 * FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
 * DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
 * OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
 * OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
/**
 * Adds advertisement tracking functions
 */
function DebuggerPlugin(logLevel) {
    if (logLevel === void 0) { logLevel = LOG_LEVEL.debug; }
    var LOG;
    var tracker;
    var eventColour;
    var colours = {
        event: function () { return "color: White; background: ".concat(eventColour, "; font-weight: bold; padding: 1px 4px; border-radius: 2px;"); },
        snowplowPurple: 'color: White; background: #6638B8; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
        activated: 'color: White; background: #9E62DD; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
        payload: 'color: White; background: #3748B8; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
        json: 'color: White; background: #388AB8; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
        schema: 'color: White; background: #268047; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
        schemaVersion: 'color: White; background: #80265F; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
    };
    var debug = function (style, message, extra) {
        var _a = extra !== null && extra !== void 0 ? extra : [''], extraMessage = _a[0], rest = _a.slice(1);
        LOG.debug.apply(LOG, __spreadArray(["v".concat(version, " %c").concat(tracker.namespace, "%c %c%s") + extraMessage,
            colours.snowplowPurple,
            '',
            style,
            message], rest, false));
    };
    function jsonInterceptor(encodeBase64) {
        var log = function (jsonType, data) {
            var schemaParts = getSchemaParts(data['schema']);
            debug(colours.event(), 'Event', [
                '%c%s%c%s%c%s\n%o',
                colours.json,
                "".concat(jsonType, ": ").concat(schemaParts ? schemaParts[1] : 'Unknown Schema'),
                colours.schemaVersion,
                schemaParts ? "Version: ".concat(schemaParts[2], "-").concat(schemaParts[3], "-").concat(schemaParts[4]) : 'Unknown Schema Version',
                colours.schema,
                schemaParts ? "Vendor: ".concat(schemaParts[0]) : 'Unknown Vendor',
                data['data'],
            ]);
        };
        return function (payloadBuilder, jsonForProcessing, contextEntitiesForProcessing) {
            if (jsonForProcessing.length) {
                var _loop_1 = function (json) {
                    var data = json.json['data'];
                    if (Array.isArray(data)) {
                        data.forEach(function (d) {
                            log(getJsonType(json), d);
                        });
                    }
                    else {
                        log(getJsonType(json), data);
                    }
                };
                for (var _i = 0, jsonForProcessing_1 = jsonForProcessing; _i < jsonForProcessing_1.length; _i++) {
                    var json = jsonForProcessing_1[_i];
                    _loop_1(json);
                }
            }
            if (contextEntitiesForProcessing.length) {
                for (var _a = 0, contextEntitiesForProcessing_1 = contextEntitiesForProcessing; _a < contextEntitiesForProcessing_1.length; _a++) {
                    var entity = contextEntitiesForProcessing_1[_a];
                    log('Context', entity);
                }
            }
            return payloadJsonProcessor(encodeBase64)(payloadBuilder, jsonForProcessing, contextEntitiesForProcessing);
        };
    }
    return {
        logger: function (logger) {
            LOG = logger;
            logger.setLogLevel(logLevel);
        },
        activateBrowserPlugin: function (t) {
            tracker = t;
            debug(colours.activated, 'Tracker Activated');
        },
        beforeTrack: function (payloadBuilder) {
            eventColour = randomcolor({ luminosity: 'dark' });
            payloadBuilder.withJsonProcessor(jsonInterceptor(tracker.core.getBase64Encoding()));
            debug(colours.event(), 'Event', ['%c%s', colours.snowplowPurple, getEventType(payloadBuilder)]);
            payloadBuilder.build();
        },
        afterTrack: function (payload) {
            debug(colours.event(), 'Event', ['%c%s\n%o', colours.payload, 'Payload', payload]);
        },
    };
}
function getJsonType(json) {
    switch (json.keyIfEncoded) {
        case 'cx':
            return 'Context';
        case 'ue_px':
            return 'Self Describing';
        default:
            return "".concat(json.keyIfEncoded, ", ").concat(json.keyIfNotEncoded);
    }
}
function getEventType(payloadBuilder) {
    var payload = payloadBuilder.getPayload();
    switch (payload['e']) {
        case 'pv':
            return 'Page View';
        case 'pp':
            return 'Page Ping';
        case 'tr':
            return 'Ecommerce Transaction';
        case 'ti':
            return 'Ecommerce Transaction Item';
        case 'se':
            return 'Structured Event';
        case 'ue':
            return 'Self Describing';
        default:
            return typeof payload['e'] === 'string' ? payload['e'] : 'Invalid';
    }
}

export { DebuggerPlugin };
//# sourceMappingURL=index.module.js.map
